/**
 * Custom error that represents a network error during an API call (e.g., "Failed to fetch").
 * Provides similar formatting to ServerError for consistent error reporting.
 */
export default class NetworkError extends Error {
    name = 'NetworkError';

    /**
     * Constructs a new instance of a NetworkError object.
     * @param {Error} originalError - The original network error (e.g., TypeError: Failed to fetch)
     * @param {string} url - The URL that failed
     */
    constructor(originalError, url) {
        // Create a clean technical message with just the essential info
        let technicalMessage = 'Network Connection Error';
        
        // Add the API route without query string for cleaner error messages
        if (url) {
            try {
                const urlObj = new URL(url);
                const baseRoute = `${urlObj.origin}${urlObj.pathname}`;
                technicalMessage += `: ${baseRoute}`;
            } catch {
                // If URL parsing fails, still remove query string manually
                const baseUrl = url.split('?')[0];
                technicalMessage += `: ${baseUrl}`;
            }
        }

        super(technicalMessage);

        // maintains proper stack trace for where our error was thrown (not available in IE)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, NetworkError);
        }

        this.url = url;
        this.originalError = originalError;
        this.isNetworkError = true;
        
        // Store network-specific details for debugging/tagging without cluttering the message
        this.networkFailureType = originalError?.message || 'Unknown network error';
        this.domain = url ? this.extractDomain(url) : null;
    }
    
    extractDomain(url) {
        try {
            return new URL(url).hostname;
        } catch {
            // Fallback for malformed URLs
            const match = url.match(/^https?:\/\/([^\/]+)/);
            return match ? match[1] : null;
        }
    }
}
